// Shared "what has this op code billed before" query — powers the billing
// surface's variance banner and "Reuse last {OpCode} wording" action
// (design_handoff_billing §4), and later an estimate-builder "start from a
// past job" path (design_handoff_estimates_mooring_pesticide). One helper so
// every caller reads billed history the same way instead of a second copy.
import { getDB } from './utilities';

export async function billedJobsByOpCode(opCode, { limit = 6, boatLengthFt = null } = {}) {
  if (!opCode) return [];

  const db = await getDB();
  const tx = db.transaction(['closedJobs'], 'readonly');
  const store = tx.objectStore('closedJobs');
  const allClosed = await new Promise((resolve, reject) => {
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });

  let matches = allClosed.filter(job => job.OpCode === opCode && job.Billing_Completed);

  // Optional narrowing for callers that care about boat size (the estimate
  // builder) — a no-op today since billing itself doesn't pass this.
  if (boatLengthFt != null) {
    const withLength = matches.filter(job =>
      job.Boat_Length != null && Math.abs(Number(job.Boat_Length) - boatLengthFt) / boatLengthFt <= 0.25
    );
    if (withLength.length) matches = withLength;
  }

  matches = matches
    .sort((a, b) => String(b.Billing_Completed).localeCompare(String(a.Billing_Completed)))
    .slice(0, limit);

  return matches.map(job => ({
    jobId: job.Job_ID || job.id,
    Boat_Name: job.Boat_Name || '',
    Boat_Length: job.Boat_Length != null ? Number(job.Boat_Length) : null,
    OpCode: job.OpCode,
    // Hours_Billed is additive/new (design_handoff_billing §3) — jobs billed
    // before it shipped won't have it, so fall back to the logged Labor hours.
    Hours_Billed: job.Hours_Billed != null
      ? Number(job.Hours_Billed)
      : Object.values(job.Labor || {}).reduce((sum, l) => sum + (Number(l.Hours) || 0), 0),
    Labor_Billed: job.Labor_Billed,
    Parts_Billed: job.Parts_Billed,
    Billing_Description: job.Billing_Description || '',
    Billing_Completed: job.Billing_Completed,
  }));
}

function median(sortedNums) {
  if (!sortedNums.length) return 0;
  const mid = Math.floor(sortedNums.length / 2);
  return sortedNums.length % 2 !== 0 ? sortedNums[mid] : (sortedNums[mid - 1] + sortedNums[mid]) / 2;
}

export function billingStats(rows) {
  const hours = rows.map(r => Number(r.Hours_Billed) || 0).filter(h => h > 0).sort((a, b) => a - b);
  const totals = rows
    .map(r => (Number(r.Labor_Billed) || 0) + (Number(r.Parts_Billed) || 0))
    .filter(t => t > 0)
    .sort((a, b) => a - b);

  return {
    medianHours: median(hours),
    medianTotal: median(totals),
    minHours: hours.length ? hours[0] : 0,
    maxHours: hours.length ? hours[hours.length - 1] : 0,
    n: rows.length,
  };
}
